import type { components } from "@/api-schema";
import { BranchIdentifierSystem, CompanyIdentifierSystem, DmsSystem, SapExportStatus } from "@/api-schema-enums";
import { Can, useAbility } from "@/casl-provider";
import { useCustomerExportMutation } from "@/common/api/customer/use-customer-export.mutation";
import { formatDateToLocale } from "@/common/helpers/format-date";
import { getSapExportOutcome, SapExportOutcome } from "@/common/helpers/sap-export-outcome";
import { toastMutationStatus } from "@/common/helpers/toast-mutation-status";
import { usePaletteCommands } from "@/components/command-palette/use-palette-commands";
import { SystemIdRow } from "@/components/detail-page/_components/system-id-row";
import { DetailTabGrid } from "@/components/detail-page/_components/tab/detail-tab-grid";
import { Badge, BadgeColorVariant, BadgeVariant } from "@/components/ui/badge";
import { Button, ButtonColorVariant, ButtonVariant } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Loader, LoaderSize, LoaderVariant } from "@/components/ui/loader";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { isOperationalCustomerCompany } from "@/modules/masterdata/company/company-identifiers";
import { ChevronDownIcon, UploadIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

type Customer = components["schemas"]["Customer"];

export const SAP_EXPORT_TAB_ID = "sap-export";

// Mirrors SAP_EXPORT_EXCLUDED_SYSTEMS in the API, which also rejects these exports.
const SAP_EXPORT_EXCLUDED_SYSTEMS: ReadonlySet<DmsSystem> = new Set([DmsSystem.ProfessionalWorks]);

const OUTCOME_LABEL_KEYS: Record<SapExportOutcome, string> = {
  [SapExportOutcome.Exported]: "aceSapExportIssues:OutcomeExported",
  [SapExportOutcome.ExportedWithMinimalPayload]: "aceSapExportIssues:OutcomeExportedWithMinimalPayload",
  [SapExportOutcome.NotExported]: "aceSapExportIssues:OutcomeNotExported",
};

const OUTCOME_COLOR_VARIANTS: Record<SapExportOutcome, BadgeColorVariant> = {
  [SapExportOutcome.Exported]: BadgeColorVariant.Success,
  [SapExportOutcome.ExportedWithMinimalPayload]: BadgeColorVariant.Warning,
  [SapExportOutcome.NotExported]: BadgeColorVariant.Error,
};

export const SapExportTab = ({ customer }: { customer: Customer }) => {
  const { t } = useTranslation(["masterdata", "aceSapExportIssues"]);
  const { mutateAsync: exportCustomer, isPending } = useCustomerExportMutation();
  const ability = useAbility();
  const sapExport = customer.sapExport?.exportStatus ? customer.sapExport : undefined;
  const isFailed = sapExport?.exportStatus === SapExportStatus.FAILED;
  const exportUnavailableReason = getExportUnavailableReason(customer, t);
  const isExcludedFromSapExport =
    customer.customerIdentifiers?.some((identifier) => SAP_EXPORT_EXCLUDED_SYSTEMS.has(identifier.type)) ?? false;

  const handleExport = async (force: boolean): Promise<void> => {
    await toastMutationStatus({
      mutationFn: () => exportCustomer({ uuid: customer.uuid, body: { force } }),
      errorMessage: t("masterdata:CustomerExportError"),
      onSuccess: () => toast.success(t("masterdata:CustomerExportQueued")),
    });
  };

  const canExport =
    ability.can("manage", "Customer") && !isExcludedFromSapExport && !exportUnavailableReason && !isPending;
  usePaletteCommands(
    canExport
      ? [
          {
            id: "customer-sap-export",
            title: t("masterdata:CustomerExport"),
            subtitle: t("masterdata:CustomerExportStandard"),
            icon: <UploadIcon />,
            keywords: ["SAP"],
            perform: () => void handleExport(false),
          },
          {
            id: "customer-sap-export-forced",
            title: t("masterdata:CustomerExport"),
            subtitle: t("masterdata:CustomerExportForced"),
            icon: <UploadIcon />,
            keywords: ["SAP"],
            perform: () => void handleExport(true),
          },
        ]
      : [],
  );

  if (isExcludedFromSapExport) {
    return (
      <span className="text-muted-foreground text-sm italic">{t("masterdata:SapExportExcludedProfessionalWorks")}</span>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <Can I="manage" a="Customer">
        <div className="flex flex-wrap items-center gap-3">
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span
                  className={exportUnavailableReason ? "inline-flex cursor-not-allowed" : "inline-flex"}
                  tabIndex={exportUnavailableReason ? 0 : undefined}
                  aria-disabled={!!exportUnavailableReason}
                >
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant={ButtonVariant.Outlined}
                        colorVariant={ButtonColorVariant.Secondary}
                        disabled={!!exportUnavailableReason || isPending}
                      >
                        {isPending ? <Loader size={LoaderSize.Small} variant={LoaderVariant.Wheel} /> : <UploadIcon />}
                        {t("masterdata:CustomerExport")}
                        <ChevronDownIcon />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="start">
                      <DropdownMenuItem onSelect={() => void handleExport(false)}>
                        {t("masterdata:CustomerExportStandard")}
                      </DropdownMenuItem>
                      <DropdownMenuItem onSelect={() => void handleExport(true)}>
                        {t("masterdata:CustomerExportForced")}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </span>
              </TooltipTrigger>
              {exportUnavailableReason && <TooltipContent>{exportUnavailableReason}</TooltipContent>}
            </Tooltip>
          </TooltipProvider>
          <span className="text-muted-foreground text-sm">{t("masterdata:CustomerForcedExportDescription")}</span>
        </div>
      </Can>
      {sapExport ? <SapExportDetails sapExport={sapExport} isFailed={isFailed} /> : <SapExportNotExportedYet />}
    </div>
  );
};

function SapExportNotExportedYet() {
  const { t } = useTranslation("masterdata");
  return <span className="text-muted-foreground text-sm italic">{t("SapExportNotExportedYet")}</span>;
}

function getExportUnavailableReason(customer: Customer, t: (key: string) => string): string | undefined {
  if (!customer.customerIdentifiers?.some((identifier) => identifier.type === DmsSystem.Sap)) {
    return t("masterdata:CustomerExportMissingSapDebitorId");
  }
  const branchCompanyUuid = customer.branch?.company?.uuid;
  const exportCompanies =
    customer.companies?.filter(
      (company) =>
        isOperationalCustomerCompany(company) || (branchCompanyUuid != null && company.uuid === branchCompanyUuid),
    ) ?? [];
  if (!exportCompanies.length) {
    return t("masterdata:CustomerExportMissingCompany");
  }
  if (!customer.branch) {
    return t("masterdata:CustomerExportMissingBranch");
  }
  if (
    !exportCompanies.every((company) =>
      company.companyIdentifiers?.some((identifier) => identifier.type === CompanyIdentifierSystem.Sap),
    )
  ) {
    return t("masterdata:CustomerExportMissingCompanySapId");
  }
  if (!customer.branch.branchIdentifiers?.some((identifier) => identifier.type === BranchIdentifierSystem.Sap)) {
    return t("masterdata:CustomerExportMissingBranchSapId");
  }
}

function SapExportDetails({
  sapExport,
  isFailed,
}: {
  sapExport: NonNullable<Customer["sapExport"]>;
  isFailed: boolean;
}) {
  const { t } = useTranslation(["masterdata", "aceSapExportIssues"]);
  const outcome = getSapExportOutcome(sapExport.exportStatus, sapExport.completionMode);

  return (
    <DetailTabGrid>
      <div className="flex flex-col gap-1 lg:col-span-2">
        <div className="flex items-center justify-between gap-4 py-1">
          <span className="text-muted-foreground text-sm">{t("masterdata:SapExportStatus")}</span>
          <Badge
            variant={BadgeVariant.Outlined}
            colorVariant={isFailed ? BadgeColorVariant.Error : BadgeColorVariant.Success}
          >
            {isFailed ? t("masterdata:SapExportStatusFailed") : t("masterdata:SapExportStatusSuccess")}
          </Badge>
        </div>
        <div className="flex items-center justify-between gap-4 py-1">
          <span className="text-muted-foreground text-sm">{t("aceSapExportIssues:ColumnOutcome")}</span>
          <Badge variant={BadgeVariant.Outlined} colorVariant={OUTCOME_COLOR_VARIANTS[outcome]}>
            {t(OUTCOME_LABEL_KEYS[outcome])}
          </Badge>
        </div>
        <SystemIdRow label={t("masterdata:SapExportLastExportAt")} value={formatDateToLocale(sapExport.lastExportAt)} />
        {isFailed && (
          <>
            <SystemIdRow
              label={t("masterdata:SapExportErrorCategory")}
              value={sapExport.errorCategory ?? undefined}
              mono
            />
            <SystemIdRow
              label={t("masterdata:SapExportErrorSapCode")}
              value={sapExport.errorSapCode ?? undefined}
              mono
            />
            <SystemIdRow
              label={t("masterdata:SapExportErrorHttpStatus")}
              value={sapExport.errorHttpStatus != null ? String(sapExport.errorHttpStatus) : undefined}
              mono
            />
            <SystemIdRow
              label={t("masterdata:SapExportErrorTransactionId")}
              value={sapExport.errorTransactionId ?? undefined}
              mono
              copyable
            />
            <SystemIdRow label={t("masterdata:SapExportErrorMessage")} value={sapExport.errorMessage ?? undefined} />
          </>
        )}
      </div>
    </DetailTabGrid>
  );
}
